// ====================================================
// src/components/block/liveStreamManagement/SourceConfigModal.tsx
//
// v2.39 — Modifications :
//   • onAddHlsSource signature étendue : (url, xtream?, label?)
//   • Le bloc "Ajouter une source" (new-hls-url / new-hls-xtream) est masqué
//     dans le <select> (onAddHlsSource n'est plus passé depuis DeckV2).
//     Le code reste intact pour une réactivation future.
//   • Tout le reste inchangé vs v2.38.

import React, { useEffect, useLayoutEffect, useCallback, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAudioLevelEffect } from '../../../hooks/liveStreamManagment/useAudioLevel';
import {
  X, Volume2, VolumeX, MonitorSpeaker, Mic, Tag,
  Activity, AlertTriangle,
  RefreshCw, Keyboard, Tv, Eye, Sun, Sparkles, RotateCcw, Settings,
} from 'lucide-react';
import { EffectCarousel, EFFECT_BUTTON_HEIGHT } from './EffectCarousel';
import { VideoSource, AudioOnlyVisual, SourceAudioConfig, JingleSlot, LayoutSlot } from '../../../contexts/MediaStreamContext';
import { SourceAliasMap } from './SourceAliasConfigModal';
import { VideoEffect } from './videoEffects/VideoEffect';
import { VIDEO_EFFECTS } from './videoEffects/registry';
import { segmentPersonInVideo, paintPersonMask } from './videoEffects/PersonSegmenter';
import { detectFaceInVideo, FaceTrackingResult } from './videoEffects/FaceTracker';
import { detectHandsInVideo, HandTrackingResult } from './videoEffects/HandTracker';
import { disposeAvatarRenderer } from './videoEffects/AvatarRenderer';
import { VideoLevels, DEFAULT_VIDEO_LEVELS, levelsAreDefault, paintProcessedFrame, WB_KELVIN_MIN, WB_KELVIN_MAX, WB_KELVIN_DEFAULT } from './videoEffects/compositeFrame';
import { applyVideoEffectsSettings, getRawStreamForEditing, VideoEffectsSettings } from './videoEffects/VideoEffectsPipelineManager';
import { useSourceRegistry } from '../../../contexts/liveStreamManagment/SourceRegistryContext';
import { useSlotAudioPreview } from '../../../hooks/liveStreamManagment/useSlotAudioPreview';
import { LevelSlider } from './LevelSlider';
import { LUFS_TARGET, useVuMeterMode, vuGradient, vuSegments } from './vuMeterZones';
import { AudioEffect } from './audioEffects/AudioEffect';
import { AUDIO_EFFECTS } from './audioEffects/registry';
import SimpleHlsPlayer from '../../widget/players/SimpleHlsPlayer';

import { HlsProbeStatus } from './useHlsProber';
import { KeyBinding, DeckKeyMap } from '../../../dataTypes/liveStreamManagment/types_deck';
import { formatKeyBinding, findConflict } from '../../../dataTypes/liveStreamManagment/utils_deck';
import { XtreamCredentials } from '../../../dataTypes/liveStreamManagment/sourceRegistry/types';



import { getLiveStreams, resolveLiveStreamUrl } from '../../../backendServices/liveStreamManagment/api_xtreamCode';
import { XtreamLiveStream } from '../../../dataTypes/liveStreamManagment/types_xtreamCode';
import XtreamStreamSelector from './XtreamStreamSelector';
import Icon from '../../widget/images/Icon';
import {
  AUDIO_ONLY_PREFIX, NEW_EMPTY_AUDIO_ONLY_VALUE, EMPTY_AUDIO_ONLY_LABEL,
  isEmptyAudioOnlySource, audioOnlySourceId, makeAudioOnlySource, makeEmptyAudioOnlySource, audioOnlyVisual,
} from '../../../dataTypes/liveStreamManagment/utils_audioOnlySource';
import { createSpectrumVideoStream } from '../../../dataTypes/liveStreamManagment/spectrumVideo';
import { useAudioMixer } from '../../../contexts/liveStreamManagment/AudioMixerContext';

// ── Types exportés ────────────────────────────────────────────────────────────

// Record ouvert, dérivé du registre (AUDIO_EFFECTS) plutôt qu'une union figée de 4
// littéraux : un nouvel effet ajouté à audioEffects/registry.ts s'y retrouve tout seul,
// sans qu'aucun type ici n'ait besoin d'être retouché — même logique que côté vidéo.
export type AudioEffectId = string;
export type AudioEffectState = Record<string, boolean>;
export const DEFAULT_AUDIO_EFFECTS: AudioEffectState = Object.fromEntries(AUDIO_EFFECTS.map(e => [e.id, false]));
export interface StereoVolume { left: number; right: number; }
export const DEFAULT_STEREO_VOLUME: StereoVolume = { left: 1, right: 1 };

// ── Props ─────────────────────────────────────────────────────────────────────

export interface SourceConfigModalProps {
  slotIndex: number;
  source: VideoSource | null;
  currentLayout?: LayoutSlot | null;
  availableSources: VideoSource[];
  /** Flux réels des sources caméra/écran/distant, pour la préview vidéo (cf. DeckV2). */
  previewStreams?: Map<string, MediaStream>;
  deckSlots: (VideoSource | null)[];
  availableAudioDevices: { deviceId: string; label: string; kind?: string }[];
  aliases: SourceAliasMap;
  onAliasChange: (sourceId: string, alias: string) => void;
  onSlotSourceChange: (slotIndex: number, newSource: VideoSource | null) => void;
  onSlotLayoutChange?: (slotIndex: number, layout: LayoutSlot | null) => void;
  selectedAudioDeviceId: string;
  onAudioDeviceChange: (sourceId: string, audioDeviceId: string) => void;
  /**
   * v5.0 — identifiant de la source à mesurer. Les niveaux ne sont plus passés
   * en props : les ChannelStrip s'abonnent à `audioLevelStore` (cf. VuMeter.tsx).
   */
  meterSourceId: string;
  stereoVolume: StereoVolume;
  onStereoVolumeChange: (sourceId: string, stereo: StereoVolume) => void;
  audioCfg: SourceAudioConfig;
  onMuteToggle: (sourceId: string) => void;
  audioEffects: AudioEffectState;
  onToggleAudioEffect: (sourceId: string, effect: AudioEffectId) => void;
  /** Réglages fins par effet (fréquence du lowcut, mix de la reverb...) — cf. AUDIO_EFFECTS. */
  audioEffectConfigs?: Record<string, unknown>;
  onAudioEffectConfigsChange?: (sourceId: string, configs: Record<string, unknown>) => void;
  /** Niveaux vidéo + effet vidéo actif + sa config, PERSISTÉS pour cette source (cf.
   * LiveStreamManager, videoEffectsSettingsMap) — dernier réglage VALIDÉ, à distinguer
   * de ce qui tourne réellement là maintenant dans le pipeline (cf.
   * applyVideoEffectsSettings) : sert UNIQUEMENT à réamorcer ce panneau à l'ouverture,
   * plutôt que de repartir des valeurs par défaut à chaque fois (constaté : "si on
   * rouvre la fenêtre de réglages, les paramètres [sont] remis à 0"). */
  videoEffectsSettings?: VideoEffectsSettings;
  onVideoEffectsSettingsChange?: (sourceId: string, settings: VideoEffectsSettings) => void;
  /** Image d'une source audio seul : écran noir ou analyseur de spectre (validé à « Valider »). */
  onAudioVisualChange?: (sourceId: string, visual: AudioOnlyVisual) => void;
  isLiveSlot: boolean;
  onSwitchLive?: (newSource: VideoSource) => void;
  onClose: () => void;
  jingleSlots?: JingleSlot[];
  layoutSlots?: LayoutSlot[];
  activeJingleId?: string | null;
  activeLayoutId?: string | null;
  onTriggerJingle?: (slot: JingleSlot) => void;
  onToggleLayout?: (slot: LayoutSlot) => void;
  hlsProbeStatus?: HlsProbeStatus;
  onRetryHls?: () => void;
  /**
   * Réacquisition forcée, posée à côté du sélecteur correspondant.
   * `onRetryHls` relance une sonde de manifeste et ne vaut que pour le HLS ;
   * celles-ci jettent l'acquisition en cours et la refont, quel que soit le type
   * de source. Recours quand la source est bien dans le deck mais que sa mini-vue
   * reste noire, ou que l'entrée audio branchée n'est pas celle qu'on attend.
   */
  onReloadSource?: () => Promise<void>;
  onReloadAudio?:  () => Promise<void>;
  keyMap: DeckKeyMap;
  onKeyMapChange: (km: DeckKeyMap) => void;
  // Signature étendue : label? permet de nommer le flux correctement dans le deck.
  // Non passé depuis DeckV2 — le bloc "Ajouter une source" est masqué.
  // Conserver pour réactivation future.
  onAddHlsSource?: (url: string, xtream?: XtreamCredentials, label?: string) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function defaultEffectConfigs(): Record<string, unknown> {
  return Object.fromEntries(VIDEO_EFFECTS.map(effect => [effect.id, effect.defaultConfig]));
}

function defaultAudioEffectConfigs(): Record<string, unknown> {
  return Object.fromEntries(AUDIO_EFFECTS.map(effect => [effect.id, effect.defaultConfig]));
}

function sourceTypeLabel(type: VideoSource['type']): string {
  switch (type) {
    case 'device': return 'caméra';
    case 'screen': return 'écran';
    case 'url':    return 'HLS';
    case 'remote': return 'distant';
    case 'audio':  return 'audio';
    default:       return '';
  }
}

const EMOJI_RE = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}\u200d\ufe0f\s]+/u;
function cleanLabel(raw: string): string { return raw.replace(EMOJI_RE, '').trim(); }

function layoutAliasKey(layoutId: string): string { return `layout:${layoutId}`; }

function initialSelectValue(source: VideoSource | null, currentLayout: LayoutSlot | null | undefined): string {
  if (currentLayout) return `scene:${currentLayout.id}`;
  return source?.id ?? '';
}

type PendingAction =
  | { type: 'swap'; newSource: VideoSource; otherSlotIndex: number }
  | { type: 'live-change'; newSource: VideoSource }
  | { type: 'swap-and-live'; newSource: VideoSource; otherSlotIndex: number };

// ── VU-mètre ──────────────────────────────────────────────────────────────────

// Échelle unique, partagée par le VU-mètre ET par la course du fader : les deux barres
// sont côte à côte et graduées pareil, donc une position de curseur se lit directement
// sur le VU. Le +6 dB est la réserve au-dessus de l'unité.
const DB_FLOOR = -60;
const DB_CEIL  =   6;

export const DB_MARKS = [6, 0, -6, -12, -18, -24, -30, -36, -42, -48, -54, -60] as const;
// Toutes les graduations sont tracées, mais une sur deux seulement est légendée : la barre
// du VU est volontairement plus courte que le fader, les libellés à 7 px s'y chevauchaient.
export const DB_LABELLED: readonly number[] = [6, 0, -12, -24, -36, -48, -60];

// Les niveaux de audioLevelStore sont DÉJÀ un pourcentage de l'échelle en dB
// (−60 → +6, cf. dbToPct dans useAudioMetering) : on les ramène en dB, sans les
// reprendre pour une amplitude linéaire. Le faire (20·log10(% / 100), avant le
// 2026-10-23) affichait −30 dB réels vers −7 dB.
export function levelPctToDB(pct: number): number {
  return DB_FLOOR + (Math.max(0, Math.min(100, pct)) / 100) * (DB_CEIL - DB_FLOOR);
}

export function dbToBarPct(db: number): number {
  return Math.max(0, Math.min(100, ((db - DB_FLOOR) / (DB_CEIL - DB_FLOOR)) * 100));
}

// ── Loi du fader L/R ─────────────────────────────────────────────────────────
// Le gain stocké (volumeLeft/Right) reste un gain LINÉAIRE : c'est ce qu'attendent
// AudioSourceRegistry.setGainLeft/Right et AudioMixerPipeline. Mais la course du slider,
// elle, ne doit pas être linéaire en amplitude — sinon 80 % du trajet ne couvrent que les
// 14 derniers dB et le VU-mètre semble ne pas bouger. Même modèle que le fader du retour
// (HlsAudioPanel) : course graduée en dB, linéaire en dB, sur EXACTEMENT la même échelle
// que le VU-mètre d'à côté (DB_FLOOR → DB_CEIL, cf. dbToBarPct) — 10 % de course = 10 % de
// barre, et le curseur se lit directement sur le VU.
// Position 0 = extinction franche (-∞), pas -60 dB.
const FADER_MIN_DB  = DB_FLOOR;
const FADER_MAX_DB  = DB_CEIL;
const FADER_SPAN_DB = FADER_MAX_DB - FADER_MIN_DB;
// Accroche l'unité : 0 dB est la valeur par défaut, elle doit être atteignable exactement
// malgré le pas du slider (sinon on se traîne un ±0,3 dB parasite sur toutes les sources).
const FADER_SNAP_DB = 0.75;
/** Position du curseur correspondant à 0 dB — valeur par défaut d'une voie. */
export const FADER_UNITY_POS = (0 - FADER_MIN_DB) / FADER_SPAN_DB;

const faderPosToDb = (p: number): number => {
  const db = FADER_MIN_DB + Math.min(1, Math.max(0, p)) * FADER_SPAN_DB;
  return Math.abs(db) < FADER_SNAP_DB ? 0 : db;
};
export const faderPosToGain = (p: number): number => (p <= 0 ? 0 : Math.pow(10, faderPosToDb(p) / 20));
export const gainToFaderPos = (g: number): number => {
  if (g <= 0) return 0;
  const db = Math.min(FADER_MAX_DB, Math.max(FADER_MIN_DB, 20 * Math.log10(g)));
  return Math.min(1, Math.max(0, (db - FADER_MIN_DB) / FADER_SPAN_DB));
};
export const formatFaderDb = (p: number): string => {
  if (p <= 0) return '−∞';
  const db = faderPosToDb(p);
  return `${db >= 0 ? '+' : ''}${db.toFixed(1)} dB`;
};

// ── Composant ─────────────────────────────────────────────────────────────────

const SourceConfigModal: React.FC<SourceConfigModalProps> = ({
  slotIndex, source, currentLayout = null,
  availableSources, previewStreams, deckSlots, availableAudioDevices,
  aliases, onAliasChange, onSlotSourceChange, onSlotLayoutChange,
  selectedAudioDeviceId, onAudioDeviceChange,
  meterSourceId, stereoVolume, onStereoVolumeChange,
  audioCfg, onMuteToggle, audioEffects, onToggleAudioEffect,
  audioEffectConfigs, onAudioEffectConfigsChange,
  videoEffectsSettings, onVideoEffectsSettingsChange, onAudioVisualChange,
  isLiveSlot, onSwitchLive, onClose,
  jingleSlots = [],
  layoutSlots = [],
  activeJingleId = null,
  activeLayoutId = null,
  onToggleLayout,
  hlsProbeStatus,
  onRetryHls, onReloadSource, onReloadAudio,
  keyMap,
  onKeyMapChange,
  onAddHlsSource,
}) => {
  // srcId — utilisé pour tous les callbacks audio
  const srcId = currentLayout?.id ?? source?.id ?? '';
  const { registry } = useSourceRegistry();
  const { audioSourceRegistry } = useAudioMixer();
  // Image d'une source audio seul, en validation différée comme la source elle-même.
  const [localVisual, setLocalVisual] = useState<AudioOnlyVisual>(() => audioOnlyVisual(source));

  // ── Snapshots à l'ouverture (pour les sections en validation différée) ────
  const initialSelectVal = initialSelectValue(source, currentLayout);
  const initialAliasKey  = currentLayout ? layoutAliasKey(currentLayout.id) : (source?.id ?? '');
  const initialAlias     = initialAliasKey ? (aliases[initialAliasKey] ?? '') : '';

  // ── States différés (source, alias, raccourcis, effets) ───────────────────
  const [selectValue,     setSelectValue]     = useState<string>(initialSelectVal);
  const [localAlias,      setLocalAlias]       = useState<string>(initialAlias);
  const [localEffects,    setLocalEffects]     = useState<AudioEffectState>({ ...audioEffects });
  const [localAudioEffectConfigs, setLocalAudioEffectConfigs] = useState<Record<string, unknown>>(
    () => ({ ...defaultAudioEffectConfigs(), ...audioEffectConfigs })
  );
  const [localKeyMap,     setLocalKeyMap]      = useState<DeckKeyMap>(keyMap);
  const [capturing,       setCapturing]        = useState<'video' | 'preview' | null>(null);
  const [captureConflict, setCaptureConflict]  = useState<string | null>(null);
  const [pending,         setPending]          = useState<PendingAction | null>(null);

  // Nouvelle source HLS (code conservé, non accessible depuis le select masqué)
  const [newHlsUrl,   setNewHlsUrl]   = useState('https://devstreaming-cdn.apple.com/videos/streaming/examples/bipbop_4x3/bipbop_4x3_variant.m3u8');
  const [newHlsError, setNewHlsError] = useState<string | null>(null);

  // ── Réglages vidéo — édités ici en local, publiés sur le VRAI flux de la source au
  // clic sur « Valider » (cf. handleConfirm → applyVideoEffectsSettings). La préview
  // ci-dessous applique les mêmes réglages en LOCAL, en continu, pendant l'édition. ──
  // Amorcés depuis `videoEffectsSettings` (dernier réglage VALIDÉ pour CETTE source,
  // cf. LiveStreamManager.videoEffectsSettingsMap) quand présent, sinon les valeurs par
  // défaut — auparavant toujours les valeurs par défaut, jamais persisté (constaté :
  // "si on rouvre la fenêtre de réglages, les paramètres [sont] remis à 0").
  const [videoLevels,    setVideoLevels]    = useState<VideoLevels>(() => videoEffectsSettings?.videoLevels ?? DEFAULT_VIDEO_LEVELS);
  const [effectConfigs,  setEffectConfigs]  = useState<Record<string, unknown>>(() => videoEffectsSettings?.effectConfigs ?? defaultEffectConfigs());
  // Au plus un effet actif PAR GROUPE (cf. VideoEffect.group) — activer un effet
  // désactive l'autre membre de son groupe, mais un effet "surroundings" (Arrière-plan/
  // Flou) et un effet "face" (Makeup/Avatar) se cumulent librement.
  const [activeEffectIds, setActiveEffectIds] = useState<string[]>(() => videoEffectsSettings?.activeEffectIds ?? []);
  const handleActivateEffect = useCallback((effect: VideoEffect<any>) => {
    setActiveEffectIds(prev => {
      if (prev.includes(effect.id)) return prev.filter(id => id !== effect.id);
      const sibling = VIDEO_EFFECTS.find(e => e.group === effect.group && prev.includes(e.id));
      const withoutSibling = sibling ? prev.filter(id => id !== sibling.id) : prev;
      return [...withoutSibling, effect.id];
    });
  }, []);
  // Effet dont la sous-fenêtre de réglage (modale au-dessus de celle-ci) est ouverte —
  // n'affecte pas activeEffectIds : ouvrir les réglages n'active pas l'effet.
  const [openEffectId,      setOpenEffectId]      = useState<string | null>(null);
  // Même principe côté effets audio — indépendant de openEffectId (deux registres distincts).
  const [openAudioEffectId, setOpenAudioEffectId] = useState<AudioEffectId | null>(null);
  // Photo du réglage de l'effet AU MOMENT où sa sous-fenêtre s'ouvre — "Annuler" dans
  // cette sous-fenêtre y revient (les tuiles s'appliquent en direct à la préview pendant
  // qu'on les essaie, cf. onChange plus bas), "Valider" n'a rien à faire de plus, le
  // réglage courant est déjà celui affiché.
  const effectConfigSnapshotRef      = useRef<unknown>(null);
  const audioEffectConfigSnapshotRef = useRef<unknown>(null);

  const handleResetVideoParams = useCallback(() => {
    setVideoLevels(DEFAULT_VIDEO_LEVELS);
    setEffectConfigs(defaultEffectConfigs());
    setActiveEffectIds([]);
  }, []);

  // Xtream (code conservé, non accessible depuis le select masqué)
  const [xtreamServer,       setXtreamServer]       = useState('http://vpn.fastservers.store');
  const [xtreamUser,         setXtreamUser]         = useState('');
  const [xtreamPass,         setXtreamPass]         = useState('');
  const [xtreamLoading,      setXtreamLoading]      = useState(false);
  const [xtreamError,        setXtreamError]        = useState<string | null>(null);
  const [xtreamStreams,      setXtreamStreams]       = useState<XtreamLiveStream[] | null>(null);
  const [showXtreamSelector, setShowXtreamSelector] = useState(false);

  // ── States temps réel audio — initialisés depuis les props ────────────────
  // Écoute locale de la fenêtre seulement (jamais persistée, jamais envoyée à l'antenne).
  // Coupée à l'ouverture : on n'entend la source qu'à la demande de l'opérateur.
  const [listenMuted,         setListenMuted]         = useState(true);
  const [realtimeMuted,       setRealtimeMuted]       = useState<boolean>(audioCfg.muted);
  const [realtimeStereo,      setRealtimeStereo]      = useState<StereoVolume>(stereoVolume);
  const [realtimeAudioDevice, setRealtimeAudioDevice] = useState<string>(selectedAudioDeviceId);

  // Chaînage L/R : quand il est enfoncé, bouger un slider déplace l'autre en conservant
  // l'écart courant. L'état ne vit que le temps de la modale (pas de persistance côté deck) :
  // la modale n'est montée que tant qu'elle est ouverte, donc chaque ouverture repart d'ici.
  // Enfoncé par défaut — régler les deux voies ensemble est le geste courant, les délier
  // reste à un clic pour le cas particulier d'un déséquilibre à corriger.
  const [stereoLinked, setStereoLinked] = useState(true);

  // Ordonnées des deux faders, MESURÉES : c'est sur elles que se branchent les bras du
  // crochet, et le maillon se pose à mi-chemin. Les faders ne sont pas au centre vertical de
  // leur bande (l'échelle en dB est en dessous), un « 50 % » du bloc tomberait trop bas.
  const stereoWrapRef  = useRef<HTMLDivElement>(null);
  const leftSliderRef  = useRef<HTMLDivElement>(null);
  const rightSliderRef = useRef<HTMLDivElement>(null);
  const [bracket, setBracket] = useState<{ topL: number; topR: number } | null>(null);

  // Resynchronisation deck → modale (pendant que la modale est ouverte)
  useEffect(() => { setRealtimeMuted(audioCfg.muted); },         [audioCfg.muted]);
  useEffect(() => { setRealtimeStereo(stereoVolume); },          [stereoVolume.left, stereoVolume.right]);
  useEffect(() => { setRealtimeAudioDevice(selectedAudioDeviceId); }, [selectedAudioDeviceId]);

  // ── Handlers audio temps réel ─────────────────────────────────────────────

  const handleMuteToggle = useCallback(() => {
    if (!srcId) return;
    onMuteToggle(srcId);
  }, [srcId, onMuteToggle]);

  // Chaînage — tout se joue en POSITION de fader (donc en dB), pas en gain linéaire :
  // c'est l'écart visible entre les deux curseurs, et l'écart perçu à l'oreille, qui doit
  // rester constant. `moved` est la voie que l'utilisateur tire, `other` celle qui suit.
  // On borne la voie tirée à la plage où la voie suivie reste dans [0, 1] : une fois celle-ci
  // collée à sa butée, la voie tirée ne bouge plus non plus — c'est bien le déplacement qui
  // est bloqué, pas seulement l'écart qui se réduit.
  const applyLink = useCallback((movedPos: number, otherPos: number, p: number): [number, number] => {
    if (!stereoLinked) return [p, otherPos];
    const gap        = otherPos - movedPos;
    const clampedMov = Math.min(Math.max(p, Math.max(0, -gap)), Math.min(1, 1 - gap));
    return [clampedMov, clampedMov + gap];
  }, [stereoLinked]);

  const leftPos  = gainToFaderPos(realtimeStereo.left);
  const rightPos = gainToFaderPos(realtimeStereo.right);

  const handlePosLeft = useCallback((p: number) => {
    if (!srcId) return;
    const [lp, rp] = applyLink(gainToFaderPos(realtimeStereo.left), gainToFaderPos(realtimeStereo.right), p);
    const next = { left: faderPosToGain(lp), right: faderPosToGain(rp) };
    setRealtimeStereo(next);
    onStereoVolumeChange(srcId, next);
  }, [srcId, realtimeStereo, onStereoVolumeChange, applyLink]);

  const handlePosRight = useCallback((p: number) => {
    if (!srcId) return;
    const [rp, lp] = applyLink(gainToFaderPos(realtimeStereo.right), gainToFaderPos(realtimeStereo.left), p);
    const next = { left: faderPosToGain(lp), right: faderPosToGain(rp) };
    setRealtimeStereo(next);
    onStereoVolumeChange(srcId, next);
  }, [srcId, realtimeStereo, onStereoVolumeChange, applyLink]);

  const handleAudioDeviceChange = useCallback((deviceId: string) => {
    if (!srcId) return;
    setRealtimeAudioDevice(deviceId);
    onAudioDeviceChange(srcId, deviceId);
  }, [srcId, onAudioDeviceChange]);

  // ── Keyboard ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (!capturing) return;
    const handler = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (['Control', 'Alt', 'Shift', 'Meta'].includes(e.key)) return;
      const newBinding: KeyBinding = {
        code: e.code, ctrl: e.ctrlKey, shift: e.shiftKey, alt: e.altKey, display: e.key,
      };
      const conflict = findConflict(localKeyMap, newBinding, capturing as keyof DeckKeyMap, slotIndex);
      setLocalKeyMap(prev => ({
        ...prev,
        [capturing]: prev[capturing as keyof DeckKeyMap].map((kb: KeyBinding | null, i: number) =>
          i === slotIndex ? newBinding : kb
        ),
      }));
      setCaptureConflict(conflict ? `Conflit avec la rangée « ${conflict.row} » slot ${conflict.idx + 1}` : null);
      setCapturing(null);
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [capturing, localKeyMap, slotIndex]);

  // ── Xtream (code conservé) ────────────────────────────────────────────────

  const handleFetchXtream = useCallback(async () => {
    if (!xtreamServer.trim() || !xtreamUser.trim() || !xtreamPass.trim()) {
      setXtreamError('Tous les champs sont requis');
      return;
    }
    setXtreamLoading(true);
    setXtreamError(null);
    const result = await getLiveStreams({
      server: xtreamServer.trim(), username: xtreamUser.trim(), password: xtreamPass.trim(),
    });
    setXtreamLoading(false);
    if (result.code !== 1 || !result.payload.length) {
      setXtreamError(result.comment || 'Aucun flux retourné');
      return;
    }
    setXtreamStreams(result.payload);
    setShowXtreamSelector(true);
  }, [xtreamServer, xtreamUser, xtreamPass]);

  const handleXtreamSelect = useCallback((stream: XtreamLiveStream) => {
    const url = resolveLiveStreamUrl(
      { server: xtreamServer.trim(), username: xtreamUser.trim(), password: xtreamPass.trim() },
      stream.stream_id
    );
    onAddHlsSource?.(url, {
      server:   xtreamServer.trim(),
      username: xtreamUser.trim(),
      password: xtreamPass.trim(),
      streamId: stream.stream_id,
    }, stream.name);   // ← label transmis
    onClose();
  }, [xtreamServer, xtreamUser, xtreamPass, onAddHlsSource, onClose]);

  // ── Sélection source (différée) ───────────────────────────────────────────

  // Une source audio seul n'existe pas avant d'avoir été posée : on la fabrique à la
  // sélection. « Vide (audio seul) » reçoit un identifiant neuf, gardé pour la durée
  // de la fenêtre — la confirmation et la validation doivent viser le même objet.
  const newEmptyAudioOnlyRef = useRef<VideoSource | null>(null);
  const resolveSelectedSource = useCallback((value: string): VideoSource | null => {
    const known = availableSources.find(s => s.id === value);
    if (known) return known;
    if (value === NEW_EMPTY_AUDIO_ONLY_VALUE) {
      if (!newEmptyAudioOnlyRef.current) newEmptyAudioOnlyRef.current = makeEmptyAudioOnlySource();
      return newEmptyAudioOnlyRef.current;
    }
    if (value.startsWith(AUDIO_ONLY_PREFIX)) {
      const device = availableAudioDevices.find(d => audioOnlySourceId(d.deviceId) === value);
      if (device) return makeAudioOnlySource(device.deviceId, cleanLabel(device.label) || device.deviceId);
    }
    return null;
  }, [availableSources, availableAudioDevices]);

  const handleSourceSelect = useCallback((value: string) => {
    setSelectValue(value);
    if (value.startsWith('scene:') || !value) return;
    const sourceId = source?.id ?? '';
    if (value === sourceId) return;
    const newSource = resolveSelectedSource(value);
    if (!newSource) return;
    const otherSlotIndex = deckSlots.findIndex((s, i) => i !== slotIndex && s?.id === newSource.id);
    const hasSwap = otherSlotIndex >= 0;
    if (hasSwap && isLiveSlot)       setPending({ type: 'swap-and-live', newSource, otherSlotIndex });
    else if (hasSwap)                setPending({ type: 'swap', newSource, otherSlotIndex });
    else if (isLiveSlot)             setPending({ type: 'live-change', newSource });
  }, [source, resolveSelectedSource, deckSlots, slotIndex, isLiveSlot]);

  const cancelPending  = useCallback(() => { setPending(null); setSelectValue(initialSelectVal); }, [initialSelectVal]);
  const confirmPending = useCallback(() => { setPending(null); }, []);

  // ── Valider (sections différées uniquement) ───────────────────────────────

  // ── Préview vidéo — reflète la source en cours de SÉLECTION dans le <select>
  // (avant validation), pas nécessairement la source déjà affectée au slot :
  // c'est bien ce que « Valider » posera qu'on veut voir, réglages compris.
  // Déclaré avant handleConfirm : ce dernier référence previewSource/previewStream
  // dans son tableau de dépendances, donc AVANT leur point d'exécution dans le corps
  // du composant serait une TDZ (les deux useCallback/const s'exécutent dans l'ordre
  // du fichier à chaque rendu).
  // Audio seul : rien à prévisualiser, la colonne de droite reste vide.
  const selectedIsAudioOnly = selectValue.startsWith(AUDIO_ONLY_PREFIX);
  const previewSource = (!selectValue || selectValue.startsWith('scene:') || selectedIsAudioOnly)
    ? null
    : (availableSources.find(s => s.id === selectValue) ?? null);
  // Le MediaStream réel d'une source caméra/écran/distant ne vit PAS sur l'objet
  // VideoSource lui-même (son champ .stream n'est jamais renseigné en pratique — cf.
  // MiniScreen dans MultiviewPanelV2, qui le lit depuis la Map `previewStreams` tenue par
  // LiveStreamManager, indexée par id de source). C'est cette même Map qu'on consulte ici —
  // SAUF si un pipeline d'effets est déjà actif pour cette source (réglages déjà validés
  // une première fois) : `previewStreams` porterait alors la version DÉJÀ traitée, et
  // rouvrir la modale segmenterait/composerait en aveugle par-dessus elle-même. Dans ce
  // cas on édite depuis le flux brut que le pipeline a gardé vivant pour cet usage.
  const previewStream = (previewSource ? getRawStreamForEditing(previewSource.id) : null)
    ?? (previewSource ? previewStreams?.get(previewSource.id) : undefined)
    ?? previewSource?.stream ?? null;
  // Les sources HLS ('url' — Playlist, OBS/RTMP...) n'ont jamais de MediaStream : elles sont
  // décodées en direct par hls.js sur la <video>, pas assignées via srcObject.
  const previewHlsUrl = (!previewStream && previewSource?.type === 'url') ? (previewSource.externalUrl ?? null) : null;

  const handleConfirm = useCallback(() => {
    // Cas new-hls-url (code conservé, non accessible depuis le select masqué)
    if (selectValue === 'new-hls-url') {
      const err = validateHlsUrl(newHlsUrl);
      if (err) { setNewHlsError(err); return; }
      onAddHlsSource?.(newHlsUrl.trim(), undefined, undefined);
      onClose();
      return;
    }
    if (selectValue === 'new-hls-xtream') return;

    // 1. Alias
    const aliasKey = (() => {
      if (selectValue.startsWith('scene:')) return `layout:${selectValue.slice('scene:'.length)}`;
      if (!selectValue) return '';
      return resolveSelectedSource(selectValue)?.id ?? selectValue;
    })();
    if (aliasKey) onAliasChange(aliasKey, localAlias);

    // 2. Source / layout
    if (selectValue !== initialSelectVal) {
      if (selectValue.startsWith('scene:')) {
        const id = selectValue.slice('scene:'.length);
        const layout = layoutSlots.find(l => l.id === id) ?? null;
        onSlotLayoutChange?.(slotIndex, layout);
        onSlotSourceChange(slotIndex, null);
      } else if (!selectValue) {
        onSlotLayoutChange?.(slotIndex, null);
        onSlotSourceChange(slotIndex, null);
      } else {
        if (currentLayout) onSlotLayoutChange?.(slotIndex, null);
        const resolved  = resolveSelectedSource(selectValue);
        // Source audio seul neuve : elle naît avec l'image choisie ici.
        const newSource = resolved?.type === 'audio' && !availableSources.some(s => s.id === resolved.id)
          ? { ...resolved, ...(localVisual !== 'black' ? { audioVisual: localVisual } : {}) }
          : resolved;
        onSlotSourceChange(slotIndex, newSource);
        if (newSource && isLiveSlot) onSwitchLive?.(newSource);
      }
    }

    // 2 bis. Image d'une source audio seul déjà connue (posée ici ou ailleurs).
    const chosen = resolveSelectedSource(selectValue);
    if (chosen?.type === 'audio' && availableSources.some(s => s.id === chosen.id)
        && audioOnlyVisual(chosen) !== localVisual) {
      onAudioVisualChange?.(chosen.id, localVisual);
    }

    // 3. Effets audio (validation différée)
    if (srcId) {
      (Object.keys(localEffects) as AudioEffectId[]).forEach(fx => {
        if (localEffects[fx] !== audioEffects[fx]) onToggleAudioEffect(srcId, fx);
      });
      if (onAudioEffectConfigsChange) {
        const prevConfigs = { ...defaultAudioEffectConfigs(), ...audioEffectConfigs };
        const changed = AUDIO_EFFECTS.some(effect =>
          JSON.stringify(localAudioEffectConfigs[effect.id]) !== JSON.stringify(prevConfigs[effect.id])
        );
        if (changed) onAudioEffectConfigsChange(srcId, localAudioEffectConfigs);
      }
    }

    // 4. KeyMap
    onKeyMapChange(localKeyMap);

    // 5. Réglages vidéo (niveaux + effet actif) — publiés sur le VRAI flux de la source
    // (mini-vues, préview deck, flux final si elle est à l'antenne), pas seulement la
    // préview locale. Seules les sources à MediaStream réel (caméra, écran, distant) le
    // supportent : une source HLS ('url') n'a rien à remplacer côté registry — cf.
    // VideoEffectsPipelineManager. `previewStream` couvre aussi le cas où un pipeline
    // existe déjà (il porte alors le flux brut gardé pour l'édition, pas le traité).
    if (previewSource && previewStream) {
      applyVideoEffectsSettings(registry, previewSource.id, { videoLevels, activeEffectIds, effectConfigs });
      // Persisté À PART de l'application réelle ci-dessus (cf. LiveStreamManager.
      // videoEffectsSettingsMap) — pour réamorcer CE panneau à sa prochaine ouverture,
      // même après un rechargement de page (le pipeline, lui, est en mémoire seulement).
      if (srcId) onVideoEffectsSettingsChange?.(srcId, { videoLevels, activeEffectIds, effectConfigs });
    }

    onClose();
  }, [
    selectValue, initialSelectVal, localAlias, localEffects, localAudioEffectConfigs, localKeyMap,
    newHlsUrl, srcId, currentLayout, layoutSlots, resolveSelectedSource, availableSources, localVisual, onAudioVisualChange, slotIndex, isLiveSlot,
    audioEffects, audioEffectConfigs,
    previewSource, previewStream, registry, videoLevels, activeEffectIds, effectConfigs,
    onAliasChange, onSlotSourceChange, onSlotLayoutChange, onSwitchLive,
    onToggleAudioEffect, onAudioEffectConfigsChange, onVideoEffectsSettingsChange, onKeyMapChange, onAddHlsSource, onClose,
  ]);

  const handleCancel = useCallback(() => onClose(), [onClose]);

  // ── Helpers UI ────────────────────────────────────────────────────────────

  function validateHlsUrl(url: string): string | null {
    const trimmed = url.trim();
    if (!trimmed) return 'URL requise';
    if (!/^https?:\/\/.+/i.test(trimmed)) return 'Doit commencer par http:// ou https://';
    if (!trimmed.toLowerCase().includes('m3u8')) return 'Flux HLS attendu (URL doit contenir m3u8)';
    return null;
  }

  const selectedSourceChanged = selectValue !== initialSelectVal;
  // Nom délibérément neutre : gate à la fois les réglages audio ET vidéo (Effets
  // vidéo, Niveau vidéo, Remise à zéro) — configurer l'un ou l'autre n'a aucun sens
  // tant que le slot n'a pas de source (ou scène) confirmée. S'appelait "showAudio"
  // jusqu'au 2026-09-27 : seule la partie audio en profitait, la partie vidéo restait
  // affichée (vide) pour une source pas encore choisie.
  const showSourceSettings = (!!source || !!currentLayout) && !selectedSourceChanged;
  // Source audio précise (micro, smartphone…) : l'entrée audio est fixée par la source.
  const fixedAudioOnly = source?.type === 'audio' && !isEmptyAudioOnlySource(source);

  // Écoute des réglages audio du brouillon (effets, niveaux) tant que la fenêtre est
  // ouverte : elle passe par une entrée d'aperçu propre, jamais par le bus d'antenne.
  // Les filtres ne s'appliquent réellement qu'à « Valider » (cf. handleConfirm).
  useSlotAudioPreview(srcId, showSourceSettings && !currentLayout, {
    effects: localEffects, effectConfigs: localAudioEffectConfigs,
    stereo: realtimeStereo, volume: audioCfg.volume ?? 1, muted: realtimeMuted, listenMuted,
  });

  // Aperçu de l'analyseur de spectre : un flux propre à la fenêtre, arrêté à sa
  // fermeture. Il lit l'entrée d'écoute du brouillon (effets en cours d'essai) quand
  // elle existe, sinon l'entrée réelle — absente tant que la source n'est pas validée.
  const audioOnlyTargetId = selectedIsAudioOnly ? (resolveSelectedSource(selectValue)?.id ?? null) : null;
  const [spectrumPreview, setSpectrumPreview] = useState<MediaStream | null>(null);
  useEffect(() => {
    if (localVisual === 'black' || !audioOnlyTargetId) { setSpectrumPreview(null); return; }
    const id = audioOnlyTargetId;
    const sv = createSpectrumVideoStream(
      () => audioSourceRegistry.getAnalysers(`audio-preview-${id}`) ?? audioSourceRegistry.getAnalysers(`audio-${id}`),
      640, 360, localVisual,
    );
    setSpectrumPreview(sv.stream);
    return () => { sv.stop(); };
  }, [localVisual, audioOnlyTargetId, audioSourceRegistry]);
  const spectrumVideoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = spectrumVideoRef.current;
    if (el && el.srcObject !== spectrumPreview) el.srcObject = spectrumPreview;
  }, [spectrumPreview]);

  const previewVideoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = previewVideoRef.current;
    if (!el) return;
    if (el.srcObject !== previewStream) el.srcObject = previewStream;
  }, [previewStream]);
  const hlsPreviewVideoRef = useRef<HTMLVideoElement>(null);
  // Préview HLS toujours coupée : le son de cette source est déjà entendu ailleurs
  // (mixage régie), la dupliquer ici serait un écho, pas un aperçu. SimpleHlsPlayer
  // ne force pas `muted` lui-même dès qu'on lui passe un ref externe (cf. son code —
  // c'est pensé pour HlsAudioPanel, qui gère le son lui-même) : on le fait donc ici.
  useEffect(() => {
    const el = hlsPreviewVideoRef.current;
    if (!el) return;
    el.muted = true;
  }, [previewHlsUrl]);

  // ── Compositeur de préview — actif dès qu'un réglage s'écarte des valeurs par défaut
  // (niveaux OU effet), pas seulement quand l'effet a besoin de détourage : c'est le
  // même rendu que ce que publie VideoEffectsPipelineManager sur le vrai flux (cf.
  // paintProcessedFrame, partagé) — la préview doit rester fidèle à ce que « Valider »
  // va effectivement poser en aval.
  const activeEffectDefs = activeEffectIds
    .map(id => VIDEO_EFFECTS.find(e => e.id === id))
    .filter((e): e is VideoEffect<any> => !!e);
  const settingsNonDefault = !levelsAreDefault(videoLevels) || activeEffectIds.length > 0;
  const showComposite = settingsNonDefault && (!!previewStream || !!previewHlsUrl);

  const previewCompositeCanvasRef = useRef<HTMLCanvasElement>(null);
  const structureCanvasRef        = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const compositeScratchRef       = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const personMaskCanvasRef       = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const personAlphaRef            = useRef<HTMLCanvasElement | null>(null);
  const segmentingRef              = useRef(false);
  const faceLandmarksRef          = useRef<FaceTrackingResult[]>([]);
  const trackingRef               = useRef(false);
  const handLandmarksRef          = useRef<HandTrackingResult[]>([]);
  const handTrackingRef           = useRef(false);
  // Lus depuis la boucle RAF, hors rendu : évite de relancer l'effet (donc la boucle) à
  // chaque glissement d'un potentiomètre.
  const effectConfigsRef = useRef(effectConfigs);
  effectConfigsRef.current = effectConfigs;
  const videoLevelsRef = useRef(videoLevels);
  videoLevelsRef.current = videoLevels;
  const [segmentationLoading, setSegmentationLoading] = useState(false);

  useEffect(() => {
    if (!showComposite) { setSegmentationLoading(false); return; }
    const canvas = previewCompositeCanvasRef.current;
    const outputCtx = canvas?.getContext('2d') ?? null;
    if (!canvas || !outputCtx) return;

    let rafId = 0;
    let cancelled = false;
    personAlphaRef.current = null;
    faceLandmarksRef.current = [];
    handLandmarksRef.current = [];
    const needsSeg = activeEffectDefs.some(e => e.needsSegmentation);
    const needsTracking = activeEffectDefs.some(e => e.needsFaceTracking);
    const needsHandTracking = activeEffectDefs.some(e => e.needsHandTracking);
    setSegmentationLoading(needsSeg);

    const getFrameEl = (): HTMLVideoElement | null => {
      if (previewStream && previewVideoRef.current)   return previewVideoRef.current;
      if (previewHlsUrl && hlsPreviewVideoRef.current) return hlsPreviewVideoRef.current;
      return null;
    };

    const tick = () => {
      if (cancelled) return;
      const video = getFrameEl();
      if (video && video.videoWidth > 0 && video.readyState >= 2) {
        const w = video.videoWidth, h = video.videoHeight;
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }

        if (needsSeg) {
          if (!segmentingRef.current) {
            segmentingRef.current = true;
            segmentPersonInVideo(video, performance.now())
              .then(mask => {
                if (cancelled) return;
                if (mask) {
                  paintPersonMask(mask, personMaskCanvasRef.current);
                  personAlphaRef.current = personMaskCanvasRef.current;
                  setSegmentationLoading(false);
                }
              })
              .catch(() => {})
              .finally(() => { segmentingRef.current = false; });
          }
        } else {
          personAlphaRef.current = null;
        }

        if (needsTracking) {
          if (!trackingRef.current) {
            trackingRef.current = true;
            detectFaceInVideo(video, performance.now())
              .then(result => { if (!cancelled) faceLandmarksRef.current = result; })
              .catch(() => {})
              .finally(() => { trackingRef.current = false; });
          }
        } else {
          faceLandmarksRef.current = [];
        }

        if (needsHandTracking) {
          if (!handTrackingRef.current) {
            handTrackingRef.current = true;
            detectHandsInVideo(video, performance.now())
              .then(result => { if (!cancelled) handLandmarksRef.current = result; })
              .catch(() => {})
              .finally(() => { handTrackingRef.current = false; });
          }
        } else {
          handLandmarksRef.current = [];
        }

        paintProcessedFrame({
          outputCtx, structureCanvas: structureCanvasRef.current, width: w, height: h, frame: video,
          activeEffects: activeEffectDefs.map(effect => ({
            effect, config: effectConfigsRef.current[effect.id] ?? effect.defaultConfig,
          })),
          levels: videoLevelsRef.current,
          personAlpha: personAlphaRef.current,
          faceLandmarks: faceLandmarksRef.current,
          handLandmarks: handLandmarksRef.current,
          scratch: compositeScratchRef.current,
        });
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      personAlphaRef.current = null;
      faceLandmarksRef.current = [];
      handLandmarksRef.current = [];
      // Libère le(s) contexte(s) WebGL Avatar de cette préview tout de suite — pas
      // besoin d'attendre que les <video> soient ramassés par le GC (cf.
      // disposeAvatarRenderer) ; sans effet si l'Avatar n'était pas actif.
      if (previewVideoRef.current)    disposeAvatarRenderer(previewVideoRef.current);
      if (hlsPreviewVideoRef.current) disposeAvatarRenderer(hlsPreviewVideoRef.current);
    };
  }, [showComposite, activeEffectIds, previewStream, previewHlsUrl]);

  useLayoutEffect(() => {
    const wrap = stereoWrapRef.current;
    const l    = leftSliderRef.current;
    const r    = rightSliderRef.current;
    if (!wrap || !l || !r) { setBracket(null); return; }

    const measure = () => {
      const w = wrap.getBoundingClientRect();
      const a = l.getBoundingClientRect();
      const b = r.getBoundingClientRect();
      const next = {
        topL: a.top + a.height / 2 - w.top,
        topR: b.top + b.height / 2 - w.top,
      };
      setBracket(prev =>
        prev && Math.abs(prev.topL - next.topL) < 0.5 && Math.abs(prev.topR - next.topR) < 0.5
          ? prev
          : next
      );
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [showSourceSettings]);

  const sourceLabel = currentLayout ? cleanLabel(currentLayout.label)
    : source ? cleanLabel(source.label)
    : `Slot ${slotIndex + 1} — vide`;

  const aliasPlaceholder = currentLayout ? cleanLabel(currentLayout.label)
    : source ? cleanLabel(source.label)
    : 'Aucune source…';

  const canAlias = !!source || !!currentLayout;

  const availableScenes  = layoutSlots.filter(l => l.background.type !== 'none' || l.incrustations.length > 0);

  // Section « Sources audio » : chaque entrée audio détectée, plus la source audio seul
  // déjà posée ici si son entrée a disparu (smartphone parti) — sans quoi le <select>
  // n'aurait plus d'option pour la valeur courante et afficherait autre chose.
  const audioOptions = availableAudioDevices.map(d => ({
    id   : audioOnlySourceId(d.deviceId),
    label: cleanLabel(d.label) || `Micro ${d.deviceId.slice(0, 8)}…`,
  }));
  if (source?.type === 'audio' && !isEmptyAudioOnlySource(source) && !audioOptions.some(o => o.id === source.id)) {
    audioOptions.push({ id: source.id, label: `${cleanLabel(source.label) || source.id} (absente)` });
  }

  const OPT:  React.CSSProperties = { background: '#1a2035', color: '#e2e8f0' };
  const OGRP: React.CSSProperties = { background: '#0d1526', color: '#64748b' };

  // ── Rendu confirmation ────────────────────────────────────────────────────

  const renderConfirmation = () => {
    if (!pending) return null;
    let message: string;
    switch (pending.type) {
      case 'swap':
        message = `« ${cleanLabel(pending.newSource.label)} » est dans le slot ${pending.otherSlotIndex + 1}. Il sera déplacé ici.`;
        break;
      case 'live-change':
        message = `Ce slot est le flux final actif. La source basculera vers « ${cleanLabel(pending.newSource.label)} » à la validation.`;
        break;
      case 'swap-and-live':
        message = `« ${cleanLabel(pending.newSource.label)} » est dans le slot ${pending.otherSlotIndex + 1} et sera déplacé ici. Le direct basculera vers cette source à la validation.`;
        break;
    }
    return (
      <div style={{ margin: '12px 20px 0', padding: '12px 16px', background: 'rgba(234,88,12,0.15)', border: '1px solid rgba(234,88,12,0.4)', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <AlertTriangle style={{ width: 16, height: 16, color: '#f97316', flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#fdba74', lineHeight: 1.4 }}>{message}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={cancelPending}  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: '#94a3b8', fontSize: 11, fontWeight: 600, padding: '5px 14px', cursor: 'pointer' }}>Annuler</button>
          <button onClick={confirmPending} style={{ background: 'linear-gradient(135deg, #9a3412, #ea580c)', border: '1px solid #f97316', borderRadius: 6, color: '#fff', fontSize: 11, fontWeight: 700, padding: '5px 14px', cursor: 'pointer' }}>Confirmer</button>
        </div>
      </div>
    );
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────

  return (
    <>
      {showXtreamSelector && xtreamStreams && (
        <XtreamStreamSelector streams={xtreamStreams} onSelect={handleXtreamSelect} onClose={() => setShowXtreamSelector(false)} />
      )}
      {ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 1040, maxHeight: '90vh', boxShadow: '0 24px 48px rgba(0,0,0,0.5)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1px solid #334155', flexShrink: 0 }}>
              <MonitorSpeaker style={{ width: 18, height: 18, color: currentLayout ? '#60a5fa' : '#4ade80' }} />
              <span style={{ fontSize: 15, fontWeight: 700, color: '#fff', flex: 1 }}>
                Slot {slotIndex + 1} — <span style={{ color: '#94a3b8' }}>{sourceLabel}</span>
              </span>
              {currentLayout && <span style={{ fontSize: 9, fontWeight: 800, color: '#fff', background: '#1e40af', borderRadius: 3, padding: '2px 6px' }}>SCÈNE</span>}
              {isLiveSlot && !currentLayout && <span style={{ fontSize: 9, fontWeight: 800, color: '#fff', background: '#ef4444', borderRadius: 3, padding: '2px 6px' }}>LIVE</span>}
              <button onClick={handleCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 4, borderRadius: 6 }}>
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>

            {renderConfirmation()}

            {/* Body */}
            {/* Grille à 2 colonnes × 2 rangées explicites (pas un flex à 2 colonnes indépendantes) :
                c'est ce qui permet à "Son actif"/"Remise à zéro" de tomber à la même hauteur en bas
                de la rangée 1 (quelle que soit la colonne la plus haute — cf. leur marginTop:'auto'
                dans un wrapper étiré par le grid), et à "Effets audio"/"Effets vidéo" de démarrer à
                la même hauteur en rangée 2. Chaque bloc porte son gridColumn/gridRow explicitement :
                "Effets audio" ET "Effets vidéo" sont conditionnels (showSourceSettings), l'auto-
                placement le déciderait mal. */}
            {/* minmax(0, 1fr), pas juste 1fr (= minmax(auto, 1fr)) : sinon la colonne au
                contenu le plus large (libellés "Compresseur"/"Noise Gate" côté audio, par
                exemple) pousse sa piste au-delà de sa part équitable — les deux colonnes
                n'étaient PAS réellement égales malgré des 1fr nominalement identiques. */}
            <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', columnGap: 24, rowGap: 20, overflowY: 'auto', flex: 1 }}>
              <style>{`
                .scm-select option, .scm-select optgroup { background: #1a2035 !important; color: #e2e8f0 !important; }
                .scm-select optgroup { color: #94a3b8 !important; font-weight: 600; }
                @keyframes hls-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }
                @keyframes kbPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
              `}</style>

              {/* ── Colonne gauche, rangée 1 — réglages existants (source, raccourcis, alias, audio) ── */}
              <div style={{ gridColumn: 1, gridRow: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* ── Source affectée ── */}
              <Section label="Source affectée" icon={<MonitorSpeaker style={{ width: 14, height: 14, color: currentLayout ? '#60a5fa' : '#4ade80' }} />}>
                <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                <select className="scm-select" value={selectValue} onChange={e => handleSourceSelect(e.target.value)} style={selectStyle}>
                  <option value="" style={OPT}>— Aucune source —</option>
                  <optgroup label="Sources vidéo" style={OGRP}>
                      {availableSources.filter(s => s.type !== 'scene' && s.type !== 'screen' && s.type !== 'audio').map(s => {
                        const otherIdx = deckSlots.findIndex((ds, i) => i !== slotIndex && ds?.id === s.id);
                        return (
                          <option key={s.id} value={s.id} style={OPT}>
                            {cleanLabel(s.label) || s.id} ({sourceTypeLabel(s.type)}){otherIdx >= 0 ? ` [slot ${otherIdx + 1}]` : ''}
                          </option>
                        );
                      })}
                      {/* Image vide, entrée audio choisie plus bas. Celle déjà posée ici garde son
                          identifiant ; sinon on en crée une neuve à la validation. */}
                      <option value={isEmptyAudioOnlySource(source) ? source!.id : NEW_EMPTY_AUDIO_ONLY_VALUE} style={OPT}>
                        {EMPTY_AUDIO_ONLY_LABEL}
                      </option>
                    </optgroup>
                  {availableSources.filter(s => s.type === 'screen' && s.screenActive).length > 0 && (
                    <optgroup label="Écran" style={OGRP}>
                      {availableSources.filter(s => s.type === 'screen' && s.screenActive).map(s => {
                        const otherIdx = deckSlots.findIndex((ds, i) => i !== slotIndex && ds?.id === s.id);
                        return (
                          <option key={s.id} value={s.id} style={OPT}>
                            {cleanLabel(s.label) || s.id}{otherIdx >= 0 ? ` [slot ${otherIdx + 1}]` : ''}
                          </option>
                        );
                      })}
                    </optgroup>
                  )}
                  {availableScenes.length > 0 && (
                    <optgroup label="Scènes" style={OGRP}>
                      {availableScenes.map(l => (
                        <option key={`scene:${l.id}`} value={`scene:${l.id}`} style={OPT}>
                          {cleanLabel(l.label) || l.id}{activeLayoutId === l.id ? ' ●' : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {(audioOptions.length > 0) && (
                    <optgroup label="Sources audio" style={OGRP}>
                      {audioOptions.map(({ id, label }) => {
                        const otherIdx = deckSlots.findIndex((ds, i) => i !== slotIndex && ds?.id === id);
                        return (
                          <option key={id} value={id} style={OPT}>
                            {label}{otherIdx >= 0 ? ` [slot ${otherIdx + 1}]` : ''}
                          </option>
                        );
                      })}
                    </optgroup>
                  )}
                  {/*
                    ── BLOC "Ajouter une source" masqué ──────────────────────────────
                    onAddHlsSource n'est plus passé depuis DeckV2.
                    Ce bloc est conservé pour réactivation future.
                    Pour réactiver : passer onAddHlsSource depuis DeckV2 → SourceConfigModal.

                  {onAddHlsSource && (
                    <optgroup label="➕ Ajouter une source" style={OGRP}>
                      <option value="new-hls-url"    style={OPT}>Nouvelle source HLS depuis une URL</option>
                      <option value="new-hls-xtream" style={OPT}>Nouvelle source HLS depuis un Xtream Code</option>
                    </optgroup>
                  )}
                  */}
                </select>
                {onReloadSource && !selectedIsAudioOnly && (
                  <ReacquireButton onReload={onReloadSource} accent="#4ade80"
                    title="Réacquérir la source vidéo — jette le flux en cours et le reprend à zéro" />
                )}
                </div>

                {/* Formulaire new-hls-url (code conservé, non accessible) */}
                {selectValue === 'new-hls-url' && onAddHlsSource && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <input type="text" value={newHlsUrl} onChange={e => { setNewHlsUrl(e.target.value); setNewHlsError(null); }} placeholder="https://exemple.com/flux/index.m3u8" autoFocus style={{ ...inputStyle, borderColor: newHlsError ? '#f97316' : '#334155' }} />
                    {newHlsError && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.35)', borderRadius: 7 }}>
                        <AlertTriangle style={{ width: 12, height: 12, color: '#f97316', flexShrink: 0 }} />
                        <span style={{ fontSize: 11, color: '#fdba74' }}>{newHlsError}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Formulaire new-hls-xtream (code conservé, non accessible) */}
                {selectValue === 'new-hls-xtream' && onAddHlsSource && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input type="text" value={xtreamServer} onChange={e => { setXtreamServer(e.target.value); setXtreamError(null); }} placeholder="http://mon-serveur.com" style={{ ...inputStyle }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <input type="text"     value={xtreamUser} onChange={e => { setXtreamUser(e.target.value); setXtreamError(null); }} placeholder="Nom d'utilisateur" style={{ ...inputStyle }} />
                      <input type="password" value={xtreamPass} onChange={e => { setXtreamPass(e.target.value); setXtreamError(null); }} placeholder="Mot de passe"      style={{ ...inputStyle }} />
                    </div>
                    {xtreamError && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.35)', borderRadius: 7 }}>
                        <AlertTriangle style={{ width: 12, height: 12, color: '#f97316', flexShrink: 0 }} />
                        <span style={{ fontSize: 11, color: '#fdba74' }}>{xtreamError}</span>
                      </div>
                    )}
                    <button onClick={handleFetchXtream} disabled={xtreamLoading} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '8px 16px', borderRadius: 8, background: xtreamLoading ? 'rgba(244,114,182,0.06)' : 'rgba(244,114,182,0.12)', border: '1px solid rgba(244,114,182,0.4)', color: '#f9a8d4', fontSize: 12, fontWeight: 700, cursor: xtreamLoading ? 'not-allowed' : 'pointer' }}>
                      {xtreamLoading ? <><RefreshCw style={{ width: 13, height: 13, animation: 'hls-blink 1s linear infinite' }} /> Chargement…</> : <><Tv style={{ width: 13, height: 13 }} /> Récupérer les flux</>}
                    </button>
                  </div>
                )}

                {selectedSourceChanged && !selectValue.startsWith('scene:') && !!selectValue && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8 }}>
                    <span style={{ fontSize: 11, color: 'rgba(148,163,184,0.7)', lineHeight: 1.4 }}>Les réglages audio seront disponibles après validation et réouverture.</span>
                  </div>
                )}

                {hlsProbeStatus === 'failed' && onRetryHls && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.35)', borderRadius: 8 }}>
                    <RefreshCw style={{ width: 14, height: 14, color: '#f97316', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#fdba74', flex: 1 }}>Source HLS indisponible</span>
                    <button onClick={onRetryHls} style={{ background: 'linear-gradient(135deg,#9a3412,#ea580c)', border: '1px solid #f97316', borderRadius: 6, color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 12px', cursor: 'pointer' }}>Réessayer</button>
                  </div>
                )}

                {hlsProbeStatus === 'probing' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 8 }}>
                    <RefreshCw style={{ width: 14, height: 14, color: '#f59e0b', flexShrink: 0, animation: 'hls-blink 1s ease-in-out infinite' }} />
                    <span style={{ fontSize: 12, color: '#fcd34d' }}>Vérification du flux HLS en cours…</span>
                  </div>
                )}

                {activeJingleId && jingleSlots.some(j => j.id === activeJingleId) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#f87171', boxShadow: '0 0 6px #f87171', flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: '#fca5a5' }}>Jingle en cours : {cleanLabel(jingleSlots.find(j => j.id === activeJingleId)?.label ?? '')}</span>
                  </div>
                )}

                {(activeLayoutId || currentLayout) && (() => {
                  const displayId   = currentLayout?.id ?? activeLayoutId;
                  const displaySlot = layoutSlots.find(l => l.id === displayId);
                  if (!displaySlot) return null;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 6 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#60a5fa', boxShadow: '0 0 6px #60a5fa', flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: '#93c5fd' }}>{currentLayout ? 'Scène affectée : ' : 'Scène active : '}{cleanLabel(displaySlot.label)}</span>
                    </div>
                  );
                })()}
              </Section>

              {/* ── Raccourcis clavier ── */}
              <Section label="Raccourcis clavier" icon={<Keyboard style={{ width: 14, height: 14, color: '#f472b6' }} />}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {captureConflict && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'rgba(234,88,12,0.12)', border: '1px solid rgba(234,88,12,0.35)', borderRadius: 8 }}>
                      <AlertTriangle style={{ width: 13, height: 13, color: '#f97316', flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: '#fdba74' }}>{captureConflict}</span>
                    </div>
                  )}
                  <KeyShortcutRow label="Flux final" accentColor="#ef4444" binding={localKeyMap.video[slotIndex] ?? null} isCapturing={capturing === 'video'} onStartCapture={() => { setCapturing('video'); setCaptureConflict(null); }} onCancelCapture={() => setCapturing(null)} onClear={() => setLocalKeyMap(prev => ({ ...prev, video: prev.video.map((kb, i) => i === slotIndex ? null : kb) }))} />
                  <KeyShortcutRow label="Preview"    accentColor="#22c55e" binding={localKeyMap.preview[slotIndex] ?? null} isCapturing={capturing === 'preview'} onStartCapture={() => { setCapturing('preview'); setCaptureConflict(null); }} onCancelCapture={() => setCapturing(null)} onClear={() => setLocalKeyMap(prev => ({ ...prev, preview: prev.preview.map((kb, i) => i === slotIndex ? null : kb) }))} />
                </div>
              </Section>

              {/* ── Alias ── */}
              <Section label="Alias affiché" icon={<Tag style={{ width: 14, height: 14, color: '#60a5fa' }} />}>
                <input type="text" value={localAlias} onChange={e => setLocalAlias(e.target.value)} placeholder={aliasPlaceholder} disabled={!canAlias} style={{ ...inputStyle, opacity: canAlias ? 1 : 0.4, cursor: canAlias ? 'text' : 'not-allowed' }} />
              </Section>

              {/* ── Entrée audio — TEMPS RÉEL ──
                  Absente pour une source audio précise : son entrée est la source elle-même. */}
              {showSourceSettings && !fixedAudioOnly && (
                <Section label="Entrée audio" icon={<Mic style={{ width: 14, height: 14, color: '#facc15' }} />}>
                  <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                    <select value={realtimeAudioDevice} onChange={e => handleAudioDeviceChange(e.target.value)} style={selectStyle} className="scm-select">
                      <option value="" style={OPT}>{source?.type === 'audio' ? '— Aucune entrée —' : '— Audio par défaut —'}</option>
                      {availableAudioDevices.map(d => (
                        <option key={d.deviceId} value={d.deviceId} style={OPT}>
                          {d.label || `Micro ${d.deviceId.slice(0, 8)}…`}
                        </option>
                      ))}
                    </select>
                    {onReloadAudio && (
                      <ReacquireButton onReload={onReloadAudio} accent="#facc15"
                        title="Réacquérir l'entrée audio — rebranche le périphérique sélectionné sans le changer" />
                    )}
                  </div>
                </Section>
              )}

              {/* ── Niveaux audio — TEMPS RÉEL ── */}
              {showSourceSettings && (
                <Section
                  label="Niveaux audio"
                  icon={<Volume2 style={{ width: 14, height: 14, color: '#22d3ee' }} />}
                  action={!currentLayout && (
                    <button
                      type="button"
                      onClick={() => setListenMuted(v => !v)}
                      aria-pressed={listenMuted}
                      title={listenMuted ? 'Rétablir l\'écoute de cette source' : 'Couper l\'écoute de cette source (sans effet sur l\'antenne)'}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8,
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        background: listenMuted ? 'rgba(239,68,68,0.18)' : 'rgba(148,163,184,0.1)',
                        border: `1px solid ${listenMuted ? '#ef4444' : '#475569'}`,
                        color: listenMuted ? '#fecaca' : '#cbd5e1',
                      }}
                    >
                      {listenMuted ? <VolumeX style={{ width: 13, height: 13 }} /> : <Volume2 style={{ width: 13, height: 13 }} />}
                      Mute
                    </button>
                  )}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {/* Les deux bandes + le maillon qui les relie : wrapper positionné pour que
                        le maillon tombe pile dans la colonne des sliders, à mi-hauteur entre eux. */}
                    <div ref={stereoWrapRef} style={{ position: 'relative', display: 'flex', alignItems: 'stretch' }}>

                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: STRIP_V_GAP }}>
                        <ChannelStrip sourceId={meterSourceId} channel="L" position={leftPos}  muted={realtimeMuted} onPositionChange={handlePosLeft}  faderRef={leftSliderRef}  />
                        <ChannelStrip sourceId={meterSourceId} channel="R" position={rightPos} muted={realtimeMuted} onPositionChange={handlePosRight} faderRef={rightSliderRef} />
                      </div>

                      {/* ── Crochet de chaînage ──
                          Un bras horizontal part de chaque voie, une barre verticale les relie,
                          le maillon est posé au milieu de cette barre. Les traits passent au cyan
                          quand les voies sont liées : le trajet du lien est alors visible. */}
                      <div style={{ width: BRACKET_W, flexShrink: 0, marginLeft: STRIP_GAP, position: 'relative' }}>
                        {bracket && (() => {
                          const stroke = stereoLinked ? '#22d3ee' : 'rgba(255,255,255,0.25)';
                          const top    = Math.min(bracket.topL, bracket.topR);
                          const height = Math.abs(bracket.topR - bracket.topL);
                          return (
                            <>
                              <div style={{ position: 'absolute', left: 0, top: bracket.topL, width: BRACKET_ARM, height: 1, background: stroke }} />
                              <div style={{ position: 'absolute', left: 0, top: bracket.topR, width: BRACKET_ARM, height: 1, background: stroke }} />
                              <div style={{ position: 'absolute', left: BRACKET_ARM, top, width: 1, height, background: stroke }} />
                              <button
                                type="button"
                                onClick={() => setStereoLinked(v => !v)}
                                title={stereoLinked ? 'Voies liées — cliquer pour les délier' : 'Voies indépendantes — cliquer pour les lier'}
                                aria-pressed={stereoLinked}
                                style={{
                                  position : 'absolute',
                                  left     : BRACKET_ARM,
                                  top      : (bracket.topL + bracket.topR) / 2,
                                  transform: 'translate(-50%, -50%)',
                                  width    : CHAIN_BTN_SIZE,
                                  height   : CHAIN_BTN_SIZE,
                                  display  : 'flex',
                                  alignItems    : 'center',
                                  justifyContent: 'center',
                                  padding      : 0,
                                  borderRadius : 6,
                                  cursor       : 'pointer',
                                  // La barre verticale du crochet passe DERRIÈRE le bouton : sa teinte
                                  // « enfoncé » doit donc être posée sur un fond opaque, sinon le trait
                                  // cyan se voit au travers et semble traverser le maillon.
                                  background   : stereoLinked
                                    ? 'linear-gradient(rgba(34,211,238,0.18), rgba(34,211,238,0.18)), #0f172a'
                                    : '#0f172a',
                                  border       : `1px solid ${stereoLinked ? '#22d3ee' : 'rgba(255,255,255,0.25)'}`,
                                  boxShadow    : stereoLinked ? '0 0 8px rgba(34,211,238,0.35) inset' : 'none',
                                  transition   : 'background 0.15s, border-color 0.15s, box-shadow 0.15s',
                                }}
                              >
                                <Icon
                                  name      = "chain"
                                  width     = {CHAIN_ICON_H * 0.39}
                                  height    = {CHAIN_ICON_H}
                                  color     = {stereoLinked ? '#22d3ee' : 'rgba(255,255,255,0.35)'}
                                  hoverable = {false}
                                  clickable = {false}
                                />
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </Section>
              )}

              {/* Hors Section, poussé en bas du wrapper de rangée 1 — s'aligne avec "Remise à
                  zéro" en colonne droite, quelle que soit la colonne la plus haute. */}
              {showSourceSettings && (
                <button onClick={handleMuteToggle} style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, background: realtimeMuted ? 'linear-gradient(135deg, #7f1d1d, #991b1b)' : 'linear-gradient(135deg, #1e3a5f, #1e40af)', border: `1px solid ${realtimeMuted ? '#ef4444' : '#3b82f6'}`, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}>
                  {realtimeMuted ? <><VolumeX style={{ width: 14, height: 14 }} /> Son coupé</> : <><Volume2 style={{ width: 14, height: 14 }} /> Son actif</>}
                </button>
              )}
              </div>

              {/* ── Colonne gauche, rangée 2 — Effets audio (aligné avec Effets vidéo) ── */}
              {showSourceSettings && (
                <div style={{ gridColumn: 1, gridRow: 2 }}>
                  <Section label="Effets audio" icon={<Activity style={{ width: 14, height: 14, color: '#e879f9' }} />}>
                    <EffectCarousel>
                      {AUDIO_EFFECTS.map(fx => (
                        <AudioEffectButton
                          key={fx.id}
                          fx={fx}
                          active={!!localEffects[fx.id]}
                          onToggle={() => setLocalEffects(prev => ({ ...prev, [fx.id]: !prev[fx.id] }))}
                          onOpenSettings={() => { audioEffectConfigSnapshotRef.current = localAudioEffectConfigs[fx.id] ?? fx.defaultConfig; setOpenAudioEffectId(fx.id); }}
                        />
                      ))}
                    </EffectCarousel>
                  </Section>
                </div>
              )}

              {/* ── Colonne droite, rangée 1 — préview vidéo, niveaux ── */}
              {/* ── Colonne droite, audio seul — image générée et son aperçu ── */}
              {selectedIsAudioOnly && (
                <div style={{ gridColumn: 2, gridRow: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <Section label="Image" icon={<Tv style={{ width: 14, height: 14, color: '#818cf8' }} />}>
                    <select className="scm-select" value={localVisual} onChange={e => setLocalVisual(e.target.value as AudioOnlyVisual)} style={selectStyle}>
                      <option value="black"    style={OPT}>Écran noir</option>
                      <option value="spectrum" style={OPT}>Analyseur de spectre</option>
                      <option value="oscilloscope" style={OPT}>Oscilloscope</option>
                    </select>
                  </Section>
                  <Section label="Aperçu" icon={<Eye style={{ width: 14, height: 14, color: '#818cf8' }} />}>
                    <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#000', borderRadius: 10, overflow: 'hidden', border: '1px solid #334155' }}>
                      {localVisual !== 'black' && spectrumPreview && (
                        <video ref={spectrumVideoRef} autoPlay muted playsInline
                          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                      )}
                      {localVisual !== 'black' && audioOnlyTargetId && !audioSourceRegistry.has(`audio-${audioOnlyTargetId}`) && (
                        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 8, textAlign: 'center', fontSize: 11, color: 'rgba(148,163,184,0.7)' }}>
                          Le son s'affichera après validation.
                        </div>
                      )}
                    </div>
                  </Section>
                </div>
              )}
              {!selectedIsAudioOnly && (
              <div style={{ gridColumn: 2, gridRow: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>

                <Section label="Aperçu" icon={<Eye style={{ width: 14, height: 14, color: '#818cf8' }} />}>
                  <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#000', borderRadius: 10, overflow: 'hidden', border: '1px solid #334155' }}>
                    {/* Les éléments source (vidéo brute / lecteur HLS) restent montés même masqués
                        quand le compositeur prend le relais : ce sont eux qui fournissent les
                        pixels à chaque frame (cf. getFrameEl dans l'effet du compositeur). */}
                    {previewStream && (
                      <video ref={previewVideoRef} autoPlay muted playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'contain', display: showComposite ? 'none' : 'block' }} />
                    )}
                    {previewHlsUrl && (
                      <div style={{ width: '100%', height: '100%', display: showComposite ? 'none' : 'block' }}>
                        <SimpleHlsPlayer ref={hlsPreviewVideoRef} streamUrl={previewHlsUrl} active />
                      </div>
                    )}
                    {showComposite && (
                      // Réglages déjà gravés dans les pixels (cf. paintProcessedFrame) — pas de
                      // filtre CSS ici, ce serait les appliquer deux fois.
                      <canvas ref={previewCompositeCanvasRef}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
                    )}
                    {showComposite && segmentationLoading && (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)', fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>
                        Détourage…
                      </div>
                    )}
                    {!previewStream && !previewHlsUrl && (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, fontSize: 11, color: 'rgba(148,163,184,0.5)', textAlign: 'center' }}>
                        {selectValue.startsWith('scene:') ? 'Aperçu indisponible pour une scène' : previewSource ? 'Aperçu indisponible pour cette source' : 'Aucune source à prévisualiser'}
                      </div>
                    )}
                  </div>
                </Section>

                {showSourceSettings && (
                  <>
                    <Section label="Niveau vidéo" icon={<Sun style={{ width: 14, height: 14, color: '#fbbf24' }} />}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <LevelSlider label="Luminosité"         value={videoLevels.luminosity}   onChange={v => setVideoLevels(prev => ({ ...prev, luminosity: v }))} />
                        <LevelSlider label="Contraste"          value={videoLevels.contrast}     onChange={v => setVideoLevels(prev => ({ ...prev, contrast: v }))} />
                        <LevelSlider label="Balance des blancs" value={videoLevels.whiteBalance}
                          min={WB_KELVIN_MIN} max={WB_KELVIN_MAX} defaultValue={WB_KELVIN_DEFAULT} roundTo={10}
                          format={v => `${v}K`}
                          onChange={v => setVideoLevels(prev => ({ ...prev, whiteBalance: v }))} />
                      </div>
                    </Section>

                    {/* Hors Section, poussé en bas du wrapper de rangée 1 — s'aligne avec "Son actif"
                        en colonne gauche, quelle que soit la colonne la plus haute. */}
                    <button onClick={handleResetVideoParams} style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, background: 'linear-gradient(135deg, #78350f, #b45309)', border: '1px solid #f59e0b', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      <RotateCcw style={{ width: 14, height: 14 }} /> Remise à zéro des paramètres
                    </button>
                  </>
                )}
              </div>
              )}

              {/* ── Colonne droite, rangée 2 — Effets vidéo (aligné avec Effets audio) ── */}
              {showSourceSettings && !selectedIsAudioOnly && (
                <div style={{ gridColumn: 2, gridRow: 2 }}>
                  <Section label="Effets vidéo" icon={<Sparkles style={{ width: 14, height: 14, color: '#34d399' }} />}>
                    <EffectCarousel>
                      {VIDEO_EFFECTS.map(effect => (
                        <EffectButton
                          key={effect.id}
                          effect={effect}
                          active={activeEffectIds.includes(effect.id)}
                          onActivate={() => handleActivateEffect(effect)}
                          onOpenSettings={() => { effectConfigSnapshotRef.current = effectConfigs[effect.id] ?? effect.defaultConfig; setOpenEffectId(effect.id); }}
                        />
                      ))}
                    </EffectCarousel>
                  </Section>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '10px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
              <button onClick={handleCancel}  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: '#94a3b8', fontSize: 12, fontWeight: 700, padding: '6px 20px', cursor: 'pointer' }}>Annuler</button>
              <button onClick={handleConfirm} style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.4)',   borderRadius: 6, color: '#a5b4fc', fontSize: 12, fontWeight: 700, padding: '6px 20px', cursor: 'pointer' }}>Valider</button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {(() => {
        const settingsEffect = VIDEO_EFFECTS.find(e => e.id === openEffectId);
        if (!settingsEffect) return null;
        const config = effectConfigs[settingsEffect.id] ?? settingsEffect.defaultConfig;
        const EffectIcon = settingsEffect.icon;
        return (
          <SettingsModal
            icon={<EffectIcon style={{ width: 16, height: 16, color: '#34d399' }} />}
            label={settingsEffect.label}
            onCancel={() => {
              setEffectConfigs(prev => ({ ...prev, [settingsEffect.id]: effectConfigSnapshotRef.current }));
              setOpenEffectId(null);
            }}
            onConfirm={() => setOpenEffectId(null)}
            maxWidth={settingsEffect.id === 'surroundings' ? 480 : undefined}
          >
            {settingsEffect.renderControls({ config, onChange: cfg => setEffectConfigs(prev => ({ ...prev, [settingsEffect.id]: cfg })) })}
          </SettingsModal>
        );
      })()}
      {(() => {
        const fx = AUDIO_EFFECTS.find(f => f.id === openAudioEffectId);
        if (!fx) return null;
        const FxIcon = fx.icon;
        const config = localAudioEffectConfigs[fx.id] ?? fx.defaultConfig;
        return (
          <SettingsModal
            icon={<FxIcon style={{ width: 16, height: 16, color: '#e879f9' }} />}
            label={fx.label}
            onCancel={() => {
              setLocalAudioEffectConfigs(prev => ({ ...prev, [fx.id]: audioEffectConfigSnapshotRef.current }));
              setOpenAudioEffectId(null);
            }}
            onConfirm={() => setOpenAudioEffectId(null)}
          >
            <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.5, marginBottom: fx.disabled ? 0 : 12 }}>{fx.description}</div>
            {fx.disabled ? (
              <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)' }}>{fx.disabledReason}</div>
            ) : (
              fx.renderControls({ config, onChange: cfg => setLocalAudioEffectConfigs(prev => ({ ...prev, [fx.id]: cfg })) })
            )}
          </SettingsModal>
        );
      })()}
    </>
  );
};

// ── Section ───────────────────────────────────────────────────────────────────

const Section: React.FC<{ label: string; icon: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }> = ({ label, icon, action, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{icon}<span style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e1' }}>{label}</span>{action && <span style={{ marginLeft: 'auto' }}>{action}</span>}</div>
    {children}
  </div>
);

// ── EffectButton ──────────────────────────────────────────────────────────────
// Même gabarit que la grille "Effets audio" (icône + libellé, mise en avant à
// l'engagement). Deux zones cliquables distinctes :
//   - le bouton lui-même : active/désactive l'effet (un seul actif à la fois,
//     géré par le parent via onActivate) ;
//   - l'engrenage, dans son coin : ouvre sa sous-fenêtre de réglage SANS
//     l'activer — cf. SettingsModal, une vraie modale au-dessus de celle-ci,
//     pas un panneau qui pousserait le layout.
// Un `<button>` ne pouvant pas en contenir un autre, le conteneur est un `div`
// avec role="button" ; l'engrenage est le seul vrai <button> imbriqué.

// Libellé des boutons d'effets vidéo et audio (2026-10-23) : 12 px au lieu de 10,
// deux lignes au plus, coupées proprement plutôt que débordantes.
const EFFECT_LABEL_STYLE: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, lineHeight: 1.2, textAlign: 'center', letterSpacing: '0.01em',
  maxWidth: '100%', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
};
const EFFECT_SOON_STYLE: React.CSSProperties = {
  position: 'absolute', bottom: 4, left: 0, right: 0, textAlign: 'center',
  fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em', textTransform: 'uppercase',
};

const EffectButton: React.FC<{
  effect: VideoEffect<any>; active: boolean;
  onActivate: () => void; onOpenSettings: () => void;
}> = ({ effect, active, onActivate, onOpenSettings }) => {
  const EffectIcon = effect.icon;
  return (
    <div
      role="button"
      tabIndex={effect.disabled ? -1 : 0}
      aria-disabled={effect.disabled}
      aria-pressed={active}
      onClick={() => { if (!effect.disabled) onActivate(); }}
      onKeyDown={e => {
        if (effect.disabled) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); }
      }}
      title={effect.disabled ? effect.disabledReason : (active ? `Désactiver : ${effect.label}` : `Activer : ${effect.label}`)}
      style={{
        position: 'relative', height: EFFECT_BUTTON_HEIGHT, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 5,
        padding: '8px 6px', borderRadius: 10,
        background: active ? 'linear-gradient(135deg, #065f46, #10b981)' : '#1e293b',
        border: `2px solid ${active ? '#34d399' : '#334155'}`,
        // Lisibilité (2026-10-23) : libellé inactif en #cbd5e1 (contraste ≈ 9:1 sur
        // l'ardoise, contre ≈ 5:1 pour l'ancien #94a3b8), « Bientôt » moins effacé.
        color: active ? '#ecfdf5' : '#cbd5e1',
        cursor: effect.disabled ? 'not-allowed' : 'pointer',
        opacity: effect.disabled ? 0.6 : 1,
        transition: 'all 0.15s',
        boxShadow: active ? '0 0 12px rgba(52,211,153,0.3)' : 'none',
        userSelect: 'none',
      }}
    >
      {!effect.disabled && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onOpenSettings(); }}
          title={`Régler : ${effect.label}`}
          style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, borderRadius: 5, background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', color: 'inherit' }}
        >
          <Settings style={{ width: 11, height: 11 }} />
        </button>
      )}
      <EffectIcon style={{ width: 18, height: 18 }} />
      <span style={EFFECT_LABEL_STYLE}>{effect.label}</span>
      {/* Position absolue : un badge en flux aurait poussé icône+libellé plus haut que sur
          les boutons sans badge, désalignant leur centre d'une rangée à l'autre. */}
      {effect.disabled && <span style={EFFECT_SOON_STYLE}>Bientôt</span>}
    </div>
  );
};

// ── AudioEffectButton ─────────────────────────────────────────────────────────
// Même gabarit que EffectButton côté vidéo (même EFFECT_BUTTON_HEIGHT, même engrenage,
// même grisage "Bientôt" pour un effet pas encore branché au pipeline — compresseur/
// noise gate aujourd'hui) pour que les deux grilles s'alignent. Différence : les effets
// audio ne sont pas exclusifs entre eux (contrairement à la vidéo, un seul actif à la
// fois) — chaque bouton bascule indépendamment son propre on/off.
// Exporté : repris par la fenêtre d'effets du bloc Mixeur du deck (DeckMixerEffectsModal).

export const AudioEffectButton: React.FC<{
  fx: AudioEffect<any>;
  active: boolean; onToggle: () => void; onOpenSettings: () => void;
}> = ({ fx, active, onToggle, onOpenSettings }) => {
  const FxIcon = fx.icon;
  const on = active && !fx.disabled;
  return (
    <div
      role="button"
      tabIndex={fx.disabled ? -1 : 0}
      aria-disabled={fx.disabled}
      aria-pressed={on}
      onClick={() => { if (!fx.disabled) onToggle(); }}
      onKeyDown={e => { if (fx.disabled) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      title={fx.disabled ? fx.disabledReason : fx.description}
      style={{
        position: 'relative', height: EFFECT_BUTTON_HEIGHT, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 5,
        padding: '8px 6px', borderRadius: 10,
        background: on ? 'linear-gradient(135deg, #581c87, #7c3aed)' : '#1e293b',
        border: `2px solid ${on ? '#a78bfa' : '#334155'}`,
        color: on ? '#f5f3ff' : '#cbd5e1',
        cursor: fx.disabled ? 'not-allowed' : 'pointer',
        opacity: fx.disabled ? 0.6 : 1,
        transition: 'all 0.15s',
        boxShadow: on ? '0 0 12px rgba(167,139,250,0.3)' : 'none',
        userSelect: 'none',
      }}
    >
      {!fx.disabled && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onOpenSettings(); }}
          title={`Régler : ${fx.label}`}
          style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, borderRadius: 5, background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', color: 'inherit' }}
        >
          <Settings style={{ width: 11, height: 11 }} />
        </button>
      )}
      {/* Voyant dans le coin, face à l'engrenage : il ne prend plus de hauteur au libellé. */}
      <div style={{ position: 'absolute', top: 10, left: 10, width: 7, height: 7, borderRadius: '50%', background: on ? '#c4b5fd' : '#475569', boxShadow: on ? '0 0 6px #a78bfa' : 'none' }} />
      <FxIcon style={{ width: 18, height: 18 }} />
      <span style={EFFECT_LABEL_STYLE}>{fx.label}</span>
      {fx.disabled && <span style={EFFECT_SOON_STYLE}>Bientôt</span>}
    </div>
  );
};

// ── SettingsModal ─────────────────────────────────────────────────────────────
// Sous-fenêtre de réglage d'un effet (vidéo ou audio), dans son propre portail :
// zIndex au-dessus de la fenêtre de slot (9999) pour bien s'afficher PAR-DESSUS,
// pas dedans. Générique (icône + libellé + contenu libre) pour servir aussi bien
// aux effets vidéo (contrôles réels, cf. VideoEffect.renderControls) qu'aux
// effets audio (pas encore de paramètre réglable, juste la description).

const SettingsModal: React.FC<{
  icon: React.ReactNode; label: string; onCancel: () => void; onConfirm: () => void; children: React.ReactNode; maxWidth?: number;
}> = ({ icon, label, onCancel, onConfirm, children, maxWidth = 340 }) => {
  useEffect(() => {
    // Échap == fermeture non intentionnelle : mêmes conséquences qu'un clic sur le fond,
    // donc "Annuler" (retour au réglage d'avant l'ouverture), pas "Valider".
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel]);

  return ReactDOM.createPortal(
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, zIndex: 10050, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)', border: '1px solid #334155', borderRadius: 14, width: '100%', maxWidth, boxShadow: '0 24px 48px rgba(0,0,0,0.55)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid #334155' }}>
          {icon}
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', flex: 1 }}>{label}</span>
        </div>
        <div style={{ padding: 16 }}>
          {children}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={onCancel}  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: '#94a3b8', fontSize: 12, fontWeight: 700, padding: '6px 20px', cursor: 'pointer' }}>Annuler</button>
          <button onClick={onConfirm} style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.4)',   borderRadius: 6, color: '#a5b4fc', fontSize: 12, fontWeight: 700, padding: '6px 20px', cursor: 'pointer' }}>Valider</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── KeyShortcutRow ────────────────────────────────────────────────────────────

const KeyShortcutRow: React.FC<{
  label: string; accentColor: string; binding: KeyBinding | null;
  isCapturing: boolean; onStartCapture: () => void; onCancelCapture: () => void; onClear: () => void;
}> = ({ label, accentColor, binding, isCapturing, onStartCapture, onCancelCapture, onClear }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: isCapturing ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${isCapturing ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.07)'}`, transition: 'all 0.15s' }}>
    <span style={{ width: 8, height: 8, borderRadius: '50%', background: accentColor, flexShrink: 0, boxShadow: `0 0 5px ${accentColor}80` }} />
    <span style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e1', flex: 1 }}>{label}</span>
    {isCapturing
      ? <span style={{ fontSize: 11, fontWeight: 700, color: '#a5b4fc', fontFamily: 'monospace', animation: 'kbPulse 0.8s ease-in-out infinite' }}>Appuyez sur une touche…</span>
      : <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', color: binding ? '#e2e8f0' : 'rgba(148,163,184,0.35)', background: binding ? 'rgba(255,255,255,0.08)' : 'transparent', border: binding ? '1px solid rgba(255,255,255,0.15)' : '1px solid transparent', borderRadius: 5, padding: binding ? '2px 8px' : '2px 0', minWidth: 32, textAlign: 'center' }}>{formatKeyBinding(binding)}</span>
    }
    {isCapturing
      ? <button onClick={onCancelCapture} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 5, color: '#94a3b8', fontSize: 10, fontWeight: 700, padding: '3px 10px', cursor: 'pointer' }}>Annuler</button>
      : <button onClick={onStartCapture}  style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.35)', borderRadius: 5, color: '#a5b4fc', fontSize: 10, fontWeight: 700, padding: '3px 10px', cursor: 'pointer' }}>Capturer</button>
    }
    {!isCapturing && <button onClick={onClear} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 5, color: 'rgba(148,163,184,0.5)', fontSize: 10, fontWeight: 700, padding: '3px 8px', cursor: 'pointer' }}>×</button>}
  </div>
);

// ── ChannelStrip ──────────────────────────────────────────────────────────────

// L'échelle monte à +6 dB. Zones d'une table de mixage (vuMeterZones, 2026-10-23) :
// vert ≤ −18 dB, jaune ≤ −6, orange ≤ 0, rouge au-delà (dépassement de l'unité).
// En mode LUFS, mêmes graduations lues en LUFS, zones autour de la cible (−14).
const STRIP_GRADIENT = { peak: vuGradient('to right', dbToBarPct, 'peak'), lufs: vuGradient('to right', dbToBarPct, 'lufs') };
const STRIP_SEGMENTS = vuSegments('to right', 4);

// ── Métriques de la bande L/R ────────────────────────────────────────────────
// Partagées entre ChannelStrip et le maillon de chaînage : celui-ci est positionné en
// absolu dans le wrapper des deux bandes et doit tomber au centre de la colonne des
// sliders. Toute retouche de largeur ici garde donc le maillon aligné.
// La bande est une grille de 4 colonnes : lettre | VU-mètre | fader | valeur dB.
// Le VU et le fader partagent la même largeur (1fr chacun) et la même hauteur de barre
// (BAR_H), et la première rangée est centrée : les quatre éléments tombent sur le même axe.
// Les graduations et le niveau mesuré vivent sur une seconde rangée, sous le VU.
const STRIP_GAP  = 10;  // gouttière entre colonnes
const LABEL_W    = 14;  // colonne de la lettre L/R
const DB_W       = 52;  // colonne du libellé dB (« +6.0 dB », « −∞ »)
export const BAR_H      =  8;  // hauteur de la barre du VU — et de la piste noire du fader
const SLIDER_H   = 16;  // hauteur de la poignée, > BAR_H pour qu'elle déborde de la piste
const FADER_HIT_H   = 24;  // zone sensible à la souris, plus haute que la poignée
// … et plus large qu'elle des deux côtés : aux deux butées la poignée déborde de la piste
// d'une demi-largeur, cette moitié-là n'était pas cliquable. Le débord est repris par une
// marge négative, donc la piste garde EXACTEMENT la longueur du VU-mètre.
const FADER_HIT_PAD = 8;
// Respiration supplémentaire entre le VU et le fader : les deux échelles se touchent par
// leurs extrémités (le +6 du VU face au -60 du fader) et se lisaient comme un seul nombre.
// Répartie en deux moitiés égales pour que les deux barres gardent la même longueur.
const VU_FADER_GAP = 16;
// Colonne du crochet de chaînage, à droite de la valeur dB : un bras horizontal par voie,
// une barre verticale qui les relie, et le maillon posé au milieu de cette barre.
const BRACKET_ARM = 14;
const BRACKET_W   = 30;

const CHAIN_BTN_SIZE  = 24;
const CHAIN_ICON_H    = 14;  // franchement plus petite que le bouton — ne touche pas les bords
// Le maillon est déporté dans la colonne du crochet : il ne passe plus entre les deux bandes,
// qui peuvent donc se resserrer.
const STRIP_V_GAP     = 12;

/**
 * v5.0 — le niveau n'arrive plus en prop : la bande s'abonne à `audioLevelStore`
 * pour `sourceId` et écrit la barre et le texte dB directement dans le DOM.
 * Le composant ne re-rend donc pas au rythme du son (cf. VuMeter.tsx).
 */
// Lisibilité (2026-10-23) : 10 px au lieu de 7, chiffres tabulaires d'une police
// proportionnelle (le monospace élargissait inutilement « −12 »), contraste relevé,
// vrai signe moins, et le 0 — la référence — plus appuyé que les autres.
export const DbScale: React.FC = () => (
  <div style={{ position: 'relative', height: 14 }}>
    {DB_LABELLED.map(mark => (
      <span key={mark} style={{
        position: 'absolute', left: `${dbToBarPct(mark)}%`, transform: 'translateX(-50%)',
        fontSize: 10, fontWeight: mark === 0 ? 700 : 500, fontVariantNumeric: 'tabular-nums',
        color: mark === 0 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.62)',
        lineHeight: 1, whiteSpace: 'nowrap', userSelect: 'none',
      }}>
        {mark > 0 ? `+${mark}` : mark < 0 ? `−${-mark}` : '0'}
      </span>
    ))}
  </div>
);

/**
 * Fader dessiné à la main plutôt qu'un <input type="range">.
 *
 * La poignée native s'arrête à une demi-largeur de poignée de chaque bord : butée à droite,
 * elle n'atteignait jamais le +6 dB annoncé par la graduation, et aucune graduation posée sur
 * la piste ne pouvait tomber juste. Ici la position 0→1 couvre exactement 0→100 % de la barre,
 * donc la poignée s'aligne au pixel sur les mêmes graduations que le VU-mètre d'à côté.
 */
const FADER_STEP = 0.005;

export const FaderBar: React.FC<{
  channel: string;
  position: number;
  muted: boolean;
  label: string;
  onChange: (p: number) => void;
  outerRef?: React.Ref<HTMLDivElement>;
}> = ({ channel, position, muted, label, onChange, outerRef }) => {
  const trackRef    = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  const setRefs = (el: HTMLDivElement | null) => { trackRef.current = el; };

  const posFromClientX = (clientX: number): number => {
    const el = trackRef.current;
    if (!el) return position;
    const r = el.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const delta =
      e.key === 'ArrowRight' || e.key === 'ArrowUp'   ?  FADER_STEP :
      e.key === 'ArrowLeft'  || e.key === 'ArrowDown' ? -FADER_STEP :
      e.key === 'Home' ? -1 : e.key === 'End' ? 1 : 0;
    if (!delta) return;
    e.preventDefault();
    onChange(Math.min(1, Math.max(0, position + delta)));
  };

  const shown = muted ? 0 : position;

  return (
    <div
      ref            = {outerRef}
      role           = "slider"
      tabIndex       = {0}
      aria-label     = {`Niveau voie ${channel}`}
      aria-valuemin  = {0}
      aria-valuemax  = {1}
      aria-valuenow  = {shown}
      aria-valuetext = {label}
      title          = {`${channel} : ${label} — double-clic pour revenir à 0 dB`}
      // preventDefault : sans lui le navigateur démarre sa propre sélection de texte, qui
      // dégénère en glisser-déposer natif dès qu'on bouge — d'où le curseur « interdit » et
      // le fader qui ne suit plus. On rend donc le focus explicitement, que preventDefault
      // supprimerait sinon.
      onPointerDown  = {e => { e.preventDefault(); e.currentTarget.focus(); draggingRef.current = true; e.currentTarget.setPointerCapture(e.pointerId); onChange(posFromClientX(e.clientX)); }}
      onPointerMove  = {e => { if (draggingRef.current) onChange(posFromClientX(e.clientX)); }}
      onPointerUp    = {e => { draggingRef.current = false; try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {} }}
      onPointerCancel= {() => { draggingRef.current = false; }}
      onDoubleClick  = {() => onChange(FADER_UNITY_POS)}
      onKeyDown      = {handleKeyDown}
      onDragStart    = {e => e.preventDefault()}
      draggable      = {false}
      style          = {{
        position        : 'relative',
        height          : FADER_HIT_H,
        marginLeft      : -FADER_HIT_PAD,
        marginRight     : -FADER_HIT_PAD,
        cursor          : 'pointer',
        touchAction     : 'none',
        outline         : 'none',
        userSelect      : 'none',
        WebkitUserSelect: 'none',
      }}
    >
      {/* Repère de géométrie : c'est CE bloc, et pas la zone sensible, qui définit le
          0–100 % de la course — sinon les marges de confort fausseraient la position. */}
      <div ref={setRefs} style={{ position: 'absolute', left: FADER_HIT_PAD, right: FADER_HIT_PAD, top: 0, bottom: 0 }}>
        {/* piste — même barre que le VU */}
        <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: BAR_H, background: muted ? '#111' : '#000', borderRadius: BAR_H / 2, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, width: `${shown * 100}%`, background: muted ? '#334155' : 'rgba(148,163,184,0.35)' }} />
          {DB_MARKS.map(mark => <div key={mark} style={{ position: 'absolute', left: `${dbToBarPct(mark)}%`, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.25)', pointerEvents: 'none' }} />)}
        </div>
        {/* poignée — centrée sur la position, elle atteint donc bien les deux butées */}
        <div style={{ position: 'absolute', left: `${shown * 100}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 10, height: SLIDER_H, borderRadius: 3, background: muted ? '#475569' : '#e2e8f0', boxShadow: '0 0 3px rgba(0,0,0,0.6)', pointerEvents: 'none' }} />
      </div>
    </div>
  );
};

/**
 * v5.0 — le niveau n'arrive plus en prop : la bande s'abonne à `audioLevelStore`
 * pour `sourceId` et écrit la barre directement dans le DOM.
 * Le composant ne re-rend donc pas au rythme du son (cf. VuMeter.tsx).
 */
const ChannelStrip: React.FC<{
  sourceId: string;
  channel: 'L' | 'R';
  position: number;
  muted: boolean;
  onPositionChange: (p: number) => void;
  faderRef?: React.Ref<HTMLDivElement>;
}> = ({ sourceId, channel, position, muted, onPositionChange, faderRef }) => {
  const barRef = useRef<HTMLDivElement>(null);
  const vuMode = useVuMeterMode();

  // Lu depuis la callback d'abonnement, qui s'exécute hors rendu.
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  useAudioLevelEffect(sourceId, levels => {
    const level    = channel === 'L' ? levels.left : levels.right;
    const db       = levelPctToDB(mutedRef.current ? 0 : level);
    const bar      = barRef.current;
    if (bar) bar.style.clipPath = `inset(0 ${100 - dbToBarPct(db)}% 0 0)`;
  });

  const initialDb  = levelPctToDB(0);
  const faderLabel = formatFaderDb(muted ? 0 : position);

  return (
    <div style={{
      display            : 'grid',
      gridTemplateColumns: `${LABEL_W}px 1fr 1fr ${DB_W}px`,
      columnGap          : STRIP_GAP,
      rowGap             : 4,
      alignItems         : 'center',
    }}>
      {/* ── Rangée 1 : lettre, barre du VU, barre du fader, valeur du fader ──
          Tous alignés sur le même axe horizontal (alignItems: center). */}
      <span style={{ gridColumn: 1, gridRow: 1, fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.75)', textAlign: 'center' }}>{channel}</span>

      <div style={{ gridColumn: 2, gridRow: 1, marginRight: VU_FADER_GAP / 2, height: BAR_H, background: 'rgba(255,255,255,0.06)', borderRadius: BAR_H / 2, overflow: 'hidden', position: 'relative' }}>
        {/* Zones d'une table de mixage, fixes sur l'échelle (vuMeterZones) : la barre les découvre. */}
        <div ref={barRef} style={{ position: 'absolute', inset: 0, background: STRIP_GRADIENT[vuMode], clipPath: `inset(0 ${100 - dbToBarPct(initialDb)}% 0 0)`, transition: 'clip-path 75ms' }} />
        <div style={{ position: 'absolute', inset: 0, background: STRIP_SEGMENTS, pointerEvents: 'none' }} />
        {vuMode === 'lufs' && <div title={`Cible ${LUFS_TARGET} LUFS`} style={{ position: 'absolute', left: `${dbToBarPct(LUFS_TARGET)}%`, top: -1, bottom: -1, width: 2, background: '#ffffff', pointerEvents: 'none' }} />}
        {DB_MARKS.map(mark => <div key={mark} style={{ position: 'absolute', left: `${dbToBarPct(mark)}%`, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.25)', pointerEvents: 'none' }} />)}
      </div>

      <div style={{ gridColumn: 3, gridRow: 1, marginLeft: VU_FADER_GAP / 2 }}>
        <FaderBar channel={channel} position={position} muted={muted} label={faderLabel} onChange={onPositionChange} outerRef={faderRef} />
      </div>

      <span title="Niveau du fader" style={{ gridColumn: 4, gridRow: 1, fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'rgba(255,255,255,0.85)', textAlign: 'right', whiteSpace: 'nowrap' }}>{faderLabel}</span>

      {/* ── Rangée 2 : la même échelle sous les deux barres ── */}
      <div style={{ gridColumn: 2, gridRow: 2, marginRight: VU_FADER_GAP / 2 }}><DbScale /></div>
      <div style={{ gridColumn: 3, gridRow: 2, marginLeft:  VU_FADER_GAP / 2 }}><DbScale /></div>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

/**
 * Bouton de réacquisition, posé au bord du sélecteur qu'il concerne.
 *
 * L'opération est asynchrone et sans retour visible ailleurs dans la fenêtre :
 * l'icône tourne le temps qu'elle dure, sinon l'opérateur ne sait pas si son clic
 * a été pris. Le bouton reste bloqué pendant ce temps — deux réacquisitions
 * concurrentes sur la même source se marcheraient dessus.
 */
const ReacquireButton: React.FC<{ onReload: () => Promise<void>; title: string; accent: string }> = ({ onReload, title, accent }) => {
  const [busy, setBusy] = useState(false);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const handleClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try { await onReload(); }
    catch (err) { console.warn('[SourceConfigModal] Réacquisition échouée :', err); }
    // La fenêtre peut avoir été fermée entre-temps : ne pas écrire dans un
    // composant démonté.
    finally { if (aliveRef.current) setBusy(false); }
  }, [busy, onReload]);

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      title={title}
      aria-label={title}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 36, flexShrink: 0, borderRadius: 8,
        background: busy ? 'rgba(148,163,184,0.08)' : `${accent}1f`,
        border: `1px solid ${accent}66`,
        color: accent, cursor: busy ? 'wait' : 'pointer',
      }}
    >
      <RefreshCw style={{ width: 15, height: 15, animation: busy ? 'hls-blink 1s linear infinite' : undefined }} />
    </button>
  );
};

const selectStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  background: '#0f172a', border: '1px solid #334155',
  color: '#e2e8f0', fontSize: 13, outline: 'none',
  cursor: 'pointer', appearance: 'auto', colorScheme: 'dark',
  WebkitTextFillColor: '#e2e8f0',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  background: '#0f172a', border: '1px solid #334155',
  color: '#e2e8f0', fontSize: 13, outline: 'none',
  boxSizing: 'border-box',
};

export default SourceConfigModal;